import { Suspense } from "react"
import { lazyRetry } from "../lib/lazy-retry"
import { Routes, Route } from "react-router-dom"
import { DashboardLayout } from "../components/layout/DashboardLayout"
import { RequireAuth } from "../components/system/auth"

// ============================================
// SUPERVISOR/MANAGEMENT DASHBOARD PAGES
// ============================================

// Overview Section
const TodaysActivity = lazyRetry(() => import("../features/supervisor/pages/TodaysActivity"))
const Communication = lazyRetry(() => import("../features/supervisor/pages/communication/CommunicationPage"))
const CommunicationUserPage = lazyRetry(() => import("../features/supervisor/pages/communication/CommunicationUserPage"))
const MessagingPage = lazyRetry(() => import("../features/messaging/pages/MessagingPage"))
const HumanActionsPage = lazyRetry(() => import("../features/human-actions/pages/HumanActionsPage"))
const FollowUpsPage = lazyRetry(() => import("../features/follow-ups/pages/FollowUpsPage"))
const HistoricalDaily = lazyRetry(() => import("../features/supervisor/pages/HistoricalDailyPage"))
const SupervisorClaimables = lazyRetry(() => import("../features/supervisor/pages/SupervisorClaimablesPage"))
const MonthToDate = lazyRetry(() => import("../features/month-to-date/pages/MonthToDatePage"))
const PipelineBreakdown = lazyRetry(() => import("../features/pipeline-breakdown/pages/PipelineBreakdownPage"))

// QC Section
const UserActivity = lazyRetry(() => import("../features/user-activity/pages/UserActivityPage"))
const UserEffectiveness = lazyRetry(() => import("../features/user-effectiveness/pages/UserEffectivenessPage"))
const UserPhoneCalls = lazyRetry(() => import("../features/phone-calls-users/pages/UserPhoneCallsPage"))
const UserPhoneCallsDetail = lazyRetry(() => import("../features/phone-calls-users/pages/UserPhoneCallsDetailPage"))
const ExpectedDates = lazyRetry(() => import("../features/expected-dates/pages/ExpectedDatesPage"))
const EventCalls = lazyRetry(() => import("../features/event-calls/pages/EventCallsPage"))
const ItemsCallsPage = lazyRetry(() => import("../features/items-calls/pages/ItemsCallsPage"))
const MattersOverdue = lazyRetry(() => import("../features/matters-overdue/pages/MattersOverduePage"))
const Problems = lazyRetry(() => import("../features/supervisor/pages/index").then(m => ({ default: m.Problems })))

const PhoneCallsHourly = lazyRetry(() => import("../features/hourly-call-volume/pages/PhoneCallsHourlyPage"))

// Deep Dive Section
const CallsList = lazyRetry(() => import("../features/calls/pages/CallsList"))
const CallDetail = lazyRetry(() => import("../features/calls/pages/CallDetail").then(m => ({ default: m.CallDetail })))
const SupervisorVoicemails = lazyRetry(() => import("../features/supervisor/pages/voicemails/VoicemailsDeepDive"))
const LeadsDeepDive = lazyRetry(() => import("../features/supervisor/pages/leads/LeadsDeepDive"))
const OutstandingItems = lazyRetry(() => import("../features/outstanding-items/pages/OutstandingItemsPage"))
const SupervisorGeneralItems = lazyRetry(() => import("../features/general-items/pages/GeneralItemsPage"))
const SupervisorApprovals = lazyRetry(() => import("../features/approvals/pages/ApprovalsPage"))
const SupervisorRequests = lazyRetry(() => import("../features/requests/pages/RequestsPage"))
const BookingsPage = lazyRetry(() => import("../features/bookings/pages/BookingsPage"))
const UserPipelines = lazyRetry(() => import("../features/user-pipelines/pages/UserPipelinesPage"))
const MatterAlerts = lazyRetry(() => import("../features/matter-alerts/pages/MatterAlertsPage"))
const SignaturesPage = lazyRetry(() => import("../features/signatures/pages/SignaturesPage"))
const SigningFieldAuditsPage = lazyRetry(() => import("../features/signing-field-audits/pages/SigningFieldAuditsPage"))
const TextMessages = lazyRetry(() => import("../features/text-messages/pages/TextMessagesList"))
const UndeliveredTexts = lazyRetry(() => import("../features/text-messages/pages/UndeliveredTextsList"))
const SupervisorTasks = lazyRetry(() => import("../features/supervisor-tasks/pages/SupervisorTasksPage"))
const SupervisorEvents = lazyRetry(() => import("../features/events-deep-dive/pages/EventsDeepDivePage"))
const DocumentUploads = lazyRetry(() => import("../features/document-uploads-deep-dive/pages/DocumentUploadsDeepDivePage"))
const UserNotes = lazyRetry(() => import("../features/user-notes/pages/UserNotesPage"))
const Uploads = lazyRetry(() => import("../features/uploads-deep-dive/pages/UploadsDeepDivePage"))

// Billing Section
const Payments = lazyRetry(() => import("../features/payments/pages/PaymentsPage"))
const DelinquentMatters = lazyRetry(() => import("../features/supervisor/pages/index").then(m => ({ default: m.DelinquentMatters })))
const RecurringPlans = lazyRetry(() => import("../features/recurring-plans/pages/RecurringPlansPage"))
const PlansOverTime = lazyRetry(() => import("../features/recurring-plans/pages/PlansOverTimePage"))
const ExpiredMethods = lazyRetry(() => import("../features/expired-methods/pages/ExpiredMethodsPage"))
const RevenueProjections = lazyRetry(() => import("../features/supervisor/pages/index").then(m => ({ default: m.RevenueProjections })))
const MattersWithoutPlan = lazyRetry(() => import("../features/matters-without-plan/pages/MattersWithoutPlanPage"))
const BillingAutomation = lazyRetry(() => import("../features/billing-automation/pages/BillingAutomationPage"))

// Time & Billing Section
const TimeReportsPage = lazyRetry(() => import("../features/supervisor/pages/TimeReportsPage"))
const TimeReportsDetail = lazyRetry(() => import("../features/supervisor/pages/TimeReportsDetail"))
const UserTimeLookupPage = lazyRetry(() => import("../features/supervisor/pages/UserTimeLookupPage"))
const UserBillingReportPage = lazyRetry(() => import("../features/supervisor/pages/UserBillingReportPage"))
const TimeCategoriesPage = lazyRetry(() => import("../features/supervisor/pages/TimeCategoriesPage"))

// Training Section
const TrainingOverview = lazyRetry(() => import("../features/training-oversight/pages/TrainingOverviewPage"))
const TrainingUserProgress = lazyRetry(() => import("../features/training-oversight/pages/TrainingUserProgressPage"))
const TrainingUserDetail = lazyRetry(() => import("../features/training-oversight/pages/TrainingUserDetailPage"))
const TrainingEnrollmentDetail = lazyRetry(() => import("../features/training-oversight/pages/TrainingEnrollmentDetailPage"))

// Settings and Other Pages
const SettingsPage = lazyRetry(() => import("../features/settings/pages/SettingsPage"))
const NotFound = lazyRetry(() => import("../features/errors/NotFound"))

// BK Module Section
const BkDailyReports = lazyRetry(() => import("../features/bk-modules/pages/BkDailyReportsPage"))
const BkItems = lazyRetry(() => import("../features/bk-modules/pages/BkItemsPage"))
const BkCasesFiled = lazyRetry(() => import("../features/bk-modules/pages/BkCasesFiledPage"))
const BkFilingFeeDeadlines = lazyRetry(() => import("../features/bk-modules/pages/BkFilingFeeDeadlinesPage"))
const BkHearings = lazyRetry(() => import("../features/bk-modules/pages/BkHearingsPage"))
const BkDocketSearch = lazyRetry(() => import("../features/bk-modules/pages/BkDocketSearchPage"))
const BkParsedActions = lazyRetry(() => import("../features/bk-modules/pages/BkParsedActionsPage"))
const BkTrusteeUploads = lazyRetry(() => import("../features/bk-modules/pages/BkTrusteeUploadsPage"))
const BkTrustees = lazyRetry(() => import("../features/bk-modules/pages/BkTrusteesPage"))
const BkPending13s = lazyRetry(() => import("../features/bk-modules/pages/BkPending13sPage"))
const BkPending7s = lazyRetry(() => import("../features/bk-modules/pages/BkPending7sPage"))
const BkCaseTouch = lazyRetry(() => import("../features/bk-modules/pages/BkCaseTouchPage"))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Dashboard routes wrapped in layout */}
        <Route path="/" element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }>
          {/* ==================== */}
          {/* OVERVIEW SECTION     */}
          {/* ==================== */}
          <Route index element={<TodaysActivity />} />
          <Route path="communication" element={<Communication />} />
          <Route path="communication/user/:userId" element={<CommunicationUserPage />} />
          <Route path="messaging" element={<MessagingPage />} />
          <Route path="human-actions" element={<HumanActionsPage />} />
          <Route path="human-actions/:filterType" element={<HumanActionsPage />} />
          <Route path="follow-ups" element={<FollowUpsPage />} />
          <Route path="follow-ups/:viewMode" element={<FollowUpsPage />} />
          <Route path="historical/daily" element={<HistoricalDaily />} />
          <Route path="claimables" element={<SupervisorClaimables />} />
          <Route path="month-to-date" element={<MonthToDate />} />
          <Route path="pipeline-breakdown" element={<PipelineBreakdown />} />

          {/* ==================== */}
          {/* QC SECTION           */}
          {/* ==================== */}
          <Route path="user-activity" element={<UserActivity />} />
          <Route path="user-activity/:view" element={<UserActivity />} />
          <Route path="user-effectiveness" element={<UserEffectiveness />} />
          <Route path="phone-calls-users" element={<UserPhoneCalls />} />
          <Route path="phone-calls-users/:userId" element={<UserPhoneCallsDetail />} />
          <Route path="expected-dates" element={<ExpectedDates />} />
          <Route path="event-calls" element={<EventCalls />} />
          <Route path="items-calls" element={<ItemsCallsPage />} />
          <Route path="matters-overdue" element={<MattersOverdue />} />
          <Route path="problems" element={<Problems />} />

          {/* ==================== */}
          {/* DEEP DIVE SECTION    */}
          {/* ==================== */}
          <Route path="phone-calls-hourly" element={<PhoneCallsHourly />} />
          <Route path="calls" element={<CallsList />} />
          <Route path="calls/:callId" element={<CallDetail />} />
          <Route path="voicemails" element={<SupervisorVoicemails />} />
          <Route path="voicemails/:filterType" element={<SupervisorVoicemails />} />
          <Route path="leads" element={<LeadsDeepDive />} />
          <Route path="leads/:filterType" element={<LeadsDeepDive />} />
          <Route path="outstanding-items" element={<OutstandingItems />} />
          <Route path="general-items" element={<SupervisorGeneralItems />} />
          <Route path="general-items/:filterType" element={<SupervisorGeneralItems />} />
          <Route path="approvals" element={<SupervisorApprovals />} />
          <Route path="approvals/:filterType" element={<SupervisorApprovals />} />
          <Route path="requests" element={<SupervisorRequests />} />
          <Route path="requests/:filterType" element={<SupervisorRequests />} />
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="user-pipelines" element={<UserPipelines />} />
          <Route path="matter-alerts" element={<MatterAlerts />} />
          <Route path="signatures" element={<SignaturesPage />} />
          <Route path="field-audits" element={<SigningFieldAuditsPage />} />
          <Route path="text-threads" element={<TextMessages />} />
          <Route path="undelivered-texts" element={<UndeliveredTexts />} />
          <Route path="tasks" element={<SupervisorTasks />} />
          <Route path="events" element={<SupervisorEvents />} />
          <Route path="document-uploads" element={<DocumentUploads />} />
          <Route path="user-notes" element={<UserNotes />} />
          <Route path="uploads" element={<Uploads />} />

          {/* ==================== */}
          {/* BILLING SECTION      */}
          {/* ==================== */}
          <Route path="payments" element={<Payments />} />
          <Route path="delinquent" element={<DelinquentMatters />} />
          <Route path="subscriptions" element={<RecurringPlans />} />
          <Route path="subscriptions/over-time" element={<PlansOverTime />} />
          <Route path="expired-methods" element={<ExpiredMethods />} />
          <Route path="revenue-projections" element={<RevenueProjections />} />
          <Route path="matters-without-plan" element={<MattersWithoutPlan />} />
          <Route path="billing-automation" element={<BillingAutomation />} />

          {/* ======================== */}
          {/* TIME & BILLING SECTION   */}
          {/* ======================== */}
          <Route path="time-reports" element={<TimeReportsPage />} />
          <Route path="time-reports/user/:userId" element={<TimeReportsDetail />} />
          <Route path="time-lookup" element={<UserTimeLookupPage />} />
          <Route path="user-billing-report" element={<UserBillingReportPage />} />
          <Route path="time-categories" element={<TimeCategoriesPage />} />

          {/* ==================== */}
          {/* BK MODULE SECTION    */}
          {/* ==================== */}
          <Route path="bk/daily-reports" element={<BkDailyReports />} />
          <Route path="bk/items" element={<BkItems />} />
          <Route path="bk/cases-filed" element={<BkCasesFiled />} />
          <Route path="bk/filing-fee-deadlines" element={<BkFilingFeeDeadlines />} />
          <Route path="bk/hearings" element={<BkHearings />} />
          <Route path="bk/docket-search" element={<BkDocketSearch />} />
          <Route path="bk/parsed-actions" element={<BkParsedActions />} />
          <Route path="bk/trustee-uploads" element={<BkTrusteeUploads />} />
          <Route path="bk/trustees" element={<BkTrustees />} />
          <Route path="bk/13s-pending" element={<BkPending13s />} />
          <Route path="bk/7s-pending" element={<BkPending7s />} />
          <Route path="bk/case-touch" element={<BkCaseTouch />} />

          {/* ==================== */}
          {/* TRAINING SECTION     */}
          {/* ==================== */}
          <Route path="training" element={<TrainingOverview />} />
          <Route path="training/users" element={<TrainingUserProgress />} />
          <Route path="training/users/:userId" element={<TrainingUserDetail />} />
          <Route path="training/users/:userId/enrollments/:enrollmentId" element={<TrainingEnrollmentDetail />} />

          {/* ==================== */}
          {/* OTHER PAGES          */}
          {/* ==================== */}
          <Route path="settings" element={<SettingsPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
